import { useEffect, useState, useCallback } from "react";
import {
  Dialog,
  DialogContent,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { BadgeCard } from "./BadgeDisplay";
import { Sparkles, PartyPopper, ChevronRight } from "lucide-react";
import confetti from "canvas-confetti";

export const BadgeCelebrationModal = ({ badge, open, onClose }) => {
  const [showContent, setShowContent] = useState(false);

  // Reset content when dialog is closed via onOpenChange
  const handleOpenChange = useCallback((isOpen) => {
    if (!isOpen) {
      setShowContent(false);
      onClose();
    }
  }, [onClose]);

  useEffect(() => {
    if (open && badge) {
      // Trigger confetti
      const duration = 2000;
      const end = Date.now() + duration;

      const frame = () => {
        confetti({
          particleCount: 3,
          angle: 60,
          spread: 55,
          origin: { x: 0, y: 0.6 },
          colors: ['#10b981', '#3b82f6', '#8b5cf6', '#f59e0b', '#ef4444']
        });
        confetti({
          particleCount: 3,
          angle: 120,
          spread: 55,
          origin: { x: 1, y: 0.6 },
          colors: ['#10b981', '#3b82f6', '#8b5cf6', '#f59e0b', '#ef4444']
        });

        if (Date.now() < end) {
          requestAnimationFrame(frame);
        }
      };

      frame();
      
      // Delay showing content for dramatic effect
      const timer = setTimeout(() => setShowContent(true), 300);
      return () => clearTimeout(timer);
    }
  }, [open, badge]);

  if (!badge) return null;

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-md text-center">
        <div className={`transition-all duration-500 ${showContent ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
          {/* Celebration Header */}
          <div className="flex items-center justify-center gap-2 mb-6">
            <PartyPopper className="w-6 h-6 text-amber-500" />
            <h2 className="text-2xl font-bold">New Badge Earned!</h2>
            <PartyPopper className="w-6 h-6 text-amber-500 scale-x-[-1]" />
          </div>

          {/* Badge Display */}
          <div className="flex justify-center mb-6 animate-bounce-slow">
            <BadgeCard badge={badge} earned={true} size="lg" />
          </div>

          {/* Badge Info */}
          <div className="space-y-2 mb-6">
            <h3 className="text-xl font-bold">{badge.name}</h3>
            <p className="text-muted-foreground">{badge.description}</p>
          </div>

          {/* Sparkle decoration */}
          <div className="flex items-center justify-center gap-1 text-amber-500 mb-6">
            <Sparkles className="w-4 h-4" />
            <span className="text-sm font-medium">Achievement Unlocked</span>
            <Sparkles className="w-4 h-4" />
          </div>

          {/* Continue Button */}
          <Button onClick={onClose} className="w-full gap-2">
            Continue Learning
            <ChevronRight className="w-4 h-4" />
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
